<template>
  <section
    v-if="holds.length"
    class="ex-holds"
    :aria-label="headingText"
  >
    <h4 v-if="!hideHeading">{{ headingText }}</h4>
    <ul aria-live="polite">
      <li
        v-for="row in rows"
        :key="row.hold.holdId"
      >
        <div class="txt">
          <strong>{{ row.title }}</strong>
          <span>{{ t('exclusiveUnits.holds.heldFor', { client: row.hold.clientName, left: row.left }) }}</span>
        </div>
        <slot
          name="hold-actions"
          :hold="row.hold"
        />
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import type { ExclusiveHoldRow } from '~/types/exclusives';
import { holdCountdown } from '~/utils/exclusives/holdCountdown';

const {
  holds,
  clockOffsetMs = 0,
  showProject = false,
  hideHeading = false,
} = defineProps<{
  holds: readonly ExclusiveHoldRow[];
  clockOffsetMs?: number;
  // /deals lists holds across projects, so each row names its project.
  showProject?: boolean;
  hideHeading?: boolean;
}>();

const { t } = useI18n();

// Minute resolution: the text (and the polite live region) changes at most once a minute.
const COUNTDOWN_TICK_MS = 30_000;
const nowMs = ref(Date.now());
let timer: ReturnType<typeof setInterval> | null = null;
onMounted(() => {
  timer = setInterval(() => {
    nowMs.value = Date.now();
  }, COUNTDOWN_TICK_MS);
});
onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
});

const leftText = (expiresAt: string): string => {
  const c = holdCountdown(expiresAt, nowMs.value + clockOffsetMs);
  if (c.expired) return t('exclusiveUnits.holds.ended');
  if (c.hours > 0) return t('exclusiveUnits.holds.leftHours', { hours: c.hours, minutes: c.minutes });
  return t('exclusiveUnits.holds.leftMinutes', { minutes: c.minutes });
};

const headingText = computed(() => t('exclusiveUnits.holds.heading'));

const titleText = (hold: ExclusiveHoldRow): string =>
  showProject && hold.projectName
    ? t('exclusiveUnits.holds.projectUnit', { project: hold.projectName, unit: hold.unitNumber })
    : t('exclusiveUnits.holds.unit', { unit: hold.unitNumber });

const rows = computed(() => holds.map((hold) => ({ hold, title: titleText(hold), left: leftText(hold.expiresAt) })));
</script>

<style scoped lang="scss">
.ex-holds {
  margin-bottom: 16px;

  h4 {
    font-size: 13px;
    font-weight: 700;
    margin: 0 0 8px;
    color: #0d0f15;
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  li {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 12px 14px;
    border-radius: 12px;
    background: #f8f7f3;
    border: 1px solid rgba(13, 15, 21, 0.08);
  }

  .txt {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 13px;
    color: #0d0f15;

    span {
      color: rgba(13, 15, 21, 0.7);
    }
  }
}
</style>
